import { matchPath } from "react-router-dom";
import { FRONTEND_URL_PROD } from "../../config";
import { ROUTES } from "../../routes";

const URL_REGEX = /https?:\/\/[^\s<>]+/g;

// Punctuation that usually ends the sentence around a URL rather than the URL.
const TRAILING_PUNCTUATION = /[.,;:!?'"]/;

export type LinkSegment =
  | { kind: "text"; text: string }
  | { kind: "link"; href: string };

const count = (text: string, char: string): number =>
  text.split(char).length - 1;

// Drop trailing punctuation, and a closing paren the URL didn't open, so
// "(see https://x.com/a)." links to "https://x.com/a".
const trimUrl = (url: string): string => {
  let trimmed = url;
  for (;;) {
    const last = trimmed.slice(-1);
    if (TRAILING_PUNCTUATION.test(last)) {
      trimmed = trimmed.slice(0, -1);
    } else if (last === ")" && count(trimmed, ")") > count(trimmed, "(")) {
      trimmed = trimmed.slice(0, -1);
    } else {
      return trimmed;
    }
  }
};

export function splitLinks(text: string): LinkSegment[] {
  const segments: LinkSegment[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(URL_REGEX)) {
    const index = match.index ?? 0;
    const href = trimUrl(match[0]);
    if (index > lastIndex) {
      segments.push({ kind: "text", text: text.slice(lastIndex, index) });
    }
    segments.push({ kind: "link", href });
    lastIndex = index + href.length;
  }
  if (lastIndex < text.length) {
    segments.push({ kind: "text", text: text.slice(lastIndex) });
  }
  return segments;
}

const REPORT_LINK_PATTERN = ROUTES.DASHBOARD_REPORT(":reportId");

export type ReportLink = {
  reportId: string;
  // In-app route (path and query) to navigate to.
  to: string;
};

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1"];

// Origins whose links count as ours when the app runs at the given origin.
// Local development also accepts prod links, so links copied from prod work
// there.
const acceptedOrigins = (origin: string): string[] =>
  LOCAL_HOSTNAMES.includes(new URL(origin).hostname)
    ? [origin, FRONTEND_URL_PROD]
    : [origin];

// Recognize a link to one of our report pages from the app at the given
// origin, or null for anything else.
export function parseReportLink(
  href: string,
  origin: string
): ReportLink | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  // The app uses HashRouter, so the route and its query live in the hash.
  if (
    !acceptedOrigins(origin).includes(url.origin) ||
    !url.hash.startsWith("#/")
  ) {
    return null;
  }
  const to = url.hash.slice(1);
  const reportId = matchPath(
    { path: REPORT_LINK_PATTERN, end: false },
    new URL(to, origin).pathname
  )?.params.reportId;
  if (!reportId) {
    return null;
  }
  return { reportId, to };
}
